import {
  AppstoreAddOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Form, Select, Tooltip } from 'antd';
import classNames from 'classnames';
import { useContext, useState } from 'react';

import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { CoverCellRoom } from 'app/components/lists/cells';
import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { RedirectIcon } from 'app/components/pages/Hotels/RedirectIcon';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetExperiencesByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelExperiences';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import type {
  PackExperienceResponse,
  PackResponse,
} from 'app/typings/core/admin/packs/pack';
import { capitalizeFirstLetter } from 'app/utils/string';

import '../ExperiencesAndRooms.scss';

import { AddExperiencesModal } from './AddExperiencesModal/AddExperiencesModal';
import { SortableExperiencesList } from './SortableExperiencesList';
import {
  DEFAULT_PAX,
  formatPrice,
  getIncludedExperiencesPrice,
} from './prices';

export const PackExperiences = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      packExperiences: PackExperienceResponse[];
      isDaytime: boolean;
    }
  >();
  const formValues = Form.useWatch([], form) ?? {};

  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: hotelExperiences } = useGetExperiencesByHotelId(
    formValues.hotelId,
    {
      withStaycationExperiences: true,
    }
  );

  const alwaysIncludedOvernightExperiences =
    hotelExperiences?.experiences?.filter(
      (exp) => exp.isAlwaysIncludedWithNight
    ) ?? [];

  const openModal = () => setIsModalVisible(true);
  const closeModal = () => setIsModalVisible(false);

  const totalPrice = getIncludedExperiencesPrice(
    formValues.packExperiences ?? [],
    hotelExperiences?.experiences ?? [],
    !!formValues.isDaytime
  );

  const renderAlwaysIncludedOvernight = () => {
    if (alwaysIncludedOvernightExperiences.length === 0) {
      return null;
    }

    return (
      <div className="er-section">
        <div className="er-section-title">Always included overnight</div>

        {alwaysIncludedOvernightExperiences.map((exp, idx) => (
          <OvernightExperienceItem
            key={exp.id}
            exp={exp}
            hasBorder={idx !== 0}
          />
        ))}
      </div>
    );
  };

  const renderSortableSection = (label: string, isIncluded: boolean) => {
    const items = (formValues?.packExperiences ?? [])
      .map((exp, index) => ({ exp, index }))
      .filter(({ exp }) => exp.isIncluded === isIncluded)
      .sort((a, b) => (a.exp.priority ?? 0) - (b.exp.priority ?? 0));

    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }) => {
      const current: PackExperienceResponse[] =
        form.getFieldValue('packExperiences') || [];

      const group: PackExperienceResponse[] = current
        .filter((e) => e.isIncluded === isIncluded)
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

      const reordered = arrayMove(group, oldIndex, newIndex).map((e, idx) => ({
        ...e,
        priority: idx,
      }));

      const merged = current.map((exp) => {
        const updated = reordered.find(
          (r) => r.experienceId === exp.experienceId
        );

        return updated ?? exp;
      });

      form.setFieldsValue({ packExperiences: merged });
      form.validateFields();
    };

    return (
      <div className="er-section">
        <div className="er-section-title">{label}</div>

        {items.length > 0 ? (
          <SortableExperiencesList
            experiences={items}
            isEditMode={isEditMode}
            onSortEnd={onSortEnd}
          />
        ) : (
          <EmptyState
            title={`No ${label.toLowerCase()} activities`}
            description="Associate an activity to this package"
            icon={<AppstoreAddOutlined />}
          />
        )}
      </div>
    );
  };

  return (
    <div className="er-card er-card-experiences">
      <div className="er-header-row">
        <div className="er-header-title">Activities</div>
        <Button
          disabled={!isEditMode}
          icon={<PlusOutlined />}
          onClick={openModal}
        >
          Select activities
        </Button>
      </div>

      <FormLineLayout>
        <Form.Item name="packExperiences" noStyle />
      </FormLineLayout>

      {!formValues.isDaytime && renderAlwaysIncludedOvernight()}
      {renderSortableSection('Included', true)}

      <div className="er-total-row">
        <div className="er-total-label">
          Total package price, for {DEFAULT_PAX} adult guests&nbsp;:
        </div>
        <div className="er-total-value">
          {formatPrice(totalPrice.min)}
          {totalPrice.max > totalPrice.min && (
            <Tooltip
              title={`Rate depends on the day (min ${formatPrice(
                totalPrice.min
              )}, max ${formatPrice(totalPrice.max)})`}
            >
              <InfoCircleOutlined />
            </Tooltip>
          )}
          {totalPrice.hasMissingPrice && (
            <Tooltip title="At least one included activity has no price configured, so it is missing from this total.">
              <InfoCircleOutlined className="er-total-warning" />
            </Tooltip>
          )}
        </div>
      </div>

      {renderSortableSection('Extras', false)}

      <AddExperiencesModal visible={isModalVisible} onClose={closeModal} />
    </div>
  );
};

type OvernightExperienceItemProps = {
  exp: ExperienceResponse;
  hasBorder: boolean;
};

const OvernightExperienceItem = ({
  exp,
  hasBorder,
}: OvernightExperienceItemProps) => {
  const { data: experienceCategories } = useExperiencesCategories();

  const experienceName = computeExperienceName({
    category: experienceCategories?.find((cat) => cat.id === exp?.categoryId),
    quantity: null,
    brand: exp?.brand || null,
    duration: exp?.duration || null,
    durationDescription: exp?.durationDescription || null,
    time: exp?.time || null,
    locations: exp?.location || null,
  });

  return (
    <div
      key={exp.id}
      className={classNames('er-item overnight-item ', {
        'er-item-wrapper': hasBorder,
      })}
    >
      <div className="er-item-main">
        <div className="er-item-left">
          <CoverCellRoom
            category={experienceName}
            entity={{ coverPicture: exp.coverPicture }}
          />
        </div>

        <div className="er-item-right">
          <span />
          <span />
          <Select className="er-select" disabled defaultValue="Included" />
          <div className="er-status">
            <span className={`er-dot ${exp.status.toLowerCase()}`} />
            {capitalizeFirstLetter(exp.status)}
          </div>
          <div className="er-item-buttons">
            <div className="er-buttons-actions">
              <span />
              <a
                href={`/activities/${exp.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <RedirectIcon />
              </a>
            </div>
            <span className="er-drag-handle-placeholder" />
          </div>
        </div>
      </div>
    </div>
  );
};
